import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDriverAuth } from "../../context/DriverAuthContext";
import { driverTasksAPI } from "../../services/driverAPI";
import "./DriverDashboard.css";

const PICKUP_TRANSITIONS = {
  scheduled: { next: "picked_up", label: "Mark Picked Up", icon: "📦" },
  picked_up: { next: "completed", label: "Mark Completed", icon: "✅" },
};

const DELIVERY_TRANSITIONS = {
  scheduled:        { next: "out_for_delivery", label: "Start Delivery", icon: "🚚" },
  out_for_delivery: { next: "delivered",        label: "Mark Delivered", icon: "✅" },
};

const STATUS_META = {
  scheduled:        { color: "#FFD60A", bg: "rgba(255,214,10,0.12)",  label: "Scheduled" },
  picked_up:        { color: "#60a5fa", bg: "rgba(59,130,246,0.15)",  label: "Picked Up" },
  completed:        { color: "#4ade80", bg: "rgba(34,197,94,0.15)",   label: "Completed" },
  out_for_delivery: { color: "#fb923c", bg: "rgba(249,115,22,0.15)",  label: "Out for Delivery" },
  delivered:        { color: "#4ade80", bg: "rgba(34,197,94,0.15)",   label: "Delivered" },
  pending:          { color: "#9ca3af", bg: "rgba(156,163,175,0.15)", label: "Pending" },
};

function StatusBadge({ status }) {
  const s = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
  });
}

/* ── Pickup Table ────────────────────────────────────────────── */
function PickupTable({ pickups, onUpdateStatus, updating }) {
  if (!pickups.length) return null;
  return (
    <div className="table-wrapper">
      <table className="task-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Task ID</th>
            <th>Sell Request</th>
            <th>Car ID</th>
            <th>Address</th>
            <th>City</th>
            <th>Date</th>
            <th>Slot</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {pickups.map((task, i) => {
            const transition = PICKUP_TRANSITIONS[task.status];
            const isUpdating = updating === task.id;
            return (
              <tr key={`p-${task.id}`} className={`task-row task-row--pickup ${task.status === "completed" ? "task-row--done" : ""}`}>
                <td className="td-index">{i + 1}</td>
                <td><span className="task-id-badge">#{task.id}</span></td>
                <td>{task.sell_request_id ? <span className="ref-badge">#{task.sell_request_id}</span> : "—"}</td>
                <td>{task.car_id ? <span className="ref-badge">#{task.car_id}</span> : "—"}</td>
                <td className="td-address">
                  <span className="addr-icon">📍</span>
                  {task.address || "—"}
                </td>
                <td>{task.city || "—"}</td>
                <td className="td-date">{formatDate(task.preferred_date || task.scheduled_date)}</td>
                <td><span className="slot-badge">{task.preferred_slot || task.time_slot || "—"}</span></td>
                <td><StatusBadge status={task.status} /></td>
                <td>
                  {transition ? (
                    <button
                      className="tbl-action-btn tbl-action-btn--pickup"
                      onClick={() => onUpdateStatus(task.id, transition.next, "pickup")}
                      disabled={isUpdating}
                    >
                      {isUpdating ? <span className="btn-spinner" /> : transition.icon}
                      {isUpdating ? " Updating…" : ` ${transition.label}`}
                    </button>
                  ) : (
                    <span className="done-chip">✅ Done</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Delivery Table ──────────────────────────────────────────── */
function DeliveryTable({ deliveries, onUpdateStatus, updating }) {
  if (!deliveries.length) return null;
  return (
    <div className="table-wrapper">
      <table className="task-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Task ID</th>
            <th>Order</th>
            <th>Car ID</th>
            <th>Address</th>
            <th>City</th>
            <th>Date</th>
            <th>Slot</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {deliveries.map((task, i) => {
            const transition = DELIVERY_TRANSITIONS[task.status];
            const isUpdating = updating === task.id;
            return (
              <tr key={`d-${task.id}`} className={`task-row task-row--delivery ${task.status === "delivered" ? "task-row--done" : ""}`}>
                <td className="td-index">{i + 1}</td>
                <td><span className="task-id-badge">#{task.id}</span></td>
                <td>{task.order_id ? <span className="ref-badge">#{task.order_id}</span> : "—"}</td>
                <td>{task.car_id ? <span className="ref-badge">#{task.car_id}</span> : "—"}</td>
                <td className="td-address">
                  <span className="addr-icon">📍</span>
                  {task.address || "—"}
                </td>
                <td>{task.city || "—"}</td>
                <td className="td-date">{formatDate(task.preferred_date || task.scheduled_date)}</td>
                <td><span className="slot-badge">{task.preferred_slot || task.time_slot || "—"}</span></td>
                <td><StatusBadge status={task.status} /></td>
                <td>
                  {transition ? (
                    <button
                      className="tbl-action-btn tbl-action-btn--delivery"
                      onClick={() => onUpdateStatus(task.id, transition.next, "delivery")}
                      disabled={isUpdating}
                    >
                      {isUpdating ? <span className="btn-spinner" /> : transition.icon}
                      {isUpdating ? " Updating…" : ` ${transition.label}`}
                    </button>
                  ) : (
                    <span className="done-chip">✅ Done</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Combined Completed Table ────────────────────────────────── */
function CompletedTable({ pickups, deliveries }) {
  const rows = [
    ...pickups.map(t => ({ ...t, _type: "pickup" })),
    ...deliveries.map(t => ({ ...t, _type: "delivery" })),
  ];
  if (!rows.length) return null;
  return (
    <div className="table-wrapper">
      <table className="task-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Task ID</th>
            <th>Reference</th>
            <th>Car ID</th>
            <th>Address</th>
            <th>City</th>
            <th>Date</th>
            <th>Slot</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((task, i) => (
            <tr key={`c-${task._type}-${task.id}`} className="task-row task-row--done">
              <td className="td-index">{i + 1}</td>
              <td>
                <span className={`type-pill type-pill--${task._type}`}>
                  {task._type === "pickup" ? "🔼 Pickup" : "🔽 Delivery"}
                </span>
              </td>
              <td><span className="task-id-badge">#{task.id}</span></td>
              <td>
                {task._type === "pickup" && task.sell_request_id
                  ? <span className="ref-badge">SR #{task.sell_request_id}</span>
                  : task._type === "delivery" && task.order_id
                  ? <span className="ref-badge">Order #{task.order_id}</span>
                  : "—"}
              </td>
              <td>{task.car_id ? <span className="ref-badge">#{task.car_id}</span> : "—"}</td>
              <td className="td-address">
                <span className="addr-icon">📍</span>{task.address || "—"}
              </td>
              <td>{task.city || "—"}</td>
              <td className="td-date">{formatDate(task.preferred_date || task.scheduled_date)}</td>
              <td><span className="slot-badge">{task.preferred_slot || task.time_slot || "—"}</span></td>
              <td><StatusBadge status={task.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Tab definitions ─────────────────────────────────────────── */
const TABS = [
  { key: "all",        label: "All Tasks",   icon: "📋" },
  { key: "active",     label: "Active",      icon: "⚡" },
  { key: "pickups",    label: "Pickups",     icon: "🔼" },
  { key: "deliveries", label: "Deliveries",  icon: "🔽" },
  { key: "completed",  label: "Completed",   icon: "✅" },
];

/* ── Dashboard ───────────────────────────────────────────────── */
export default function DriverDashboard() {
  const { driver, logout } = useDriverAuth();
  const navigate = useNavigate();

  const [tasks,     setTasks]     = useState({ pickups: [], deliveries: [] });
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [updating,  setUpdating]  = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [toast,     setToast]     = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await driverTasksAPI.getTasks();
      if (data.error) { setError(data.error); return; }
      setTasks({ pickups: data.pickups || [], deliveries: data.deliveries || [] });
    } catch {
      setError("Failed to load tasks. Check your connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpdate = async (id, status, type) => {
    setUpdating(id);
    try {
      const fn = type === "pickup" ? driverTasksAPI.updatePickup : driverTasksAPI.updateDelivery;
      const data = await fn(id, status);
      if (data.error) { showToast(data.error, false); return; }
      showToast(`Status updated to "${status}" ✓`);
      await fetchTasks();
    } catch {
      showToast("Update failed. Try again.", false);
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = () => { logout(); navigate("/driver/login"); };

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  /* Derived sets */
  const allPickups    = tasks.pickups;
  const allDeliveries = tasks.deliveries;
  const activePickups    = allPickups.filter(p => ["scheduled","picked_up"].includes(p.status));
  const activeDeliveries = allDeliveries.filter(d => ["scheduled","out_for_delivery"].includes(d.status));
  const completedPickups    = allPickups.filter(p => p.status === "completed");
  const completedDeliveries = allDeliveries.filter(d => d.status === "delivered");

  /* What to show in each tab */
  const getVisible = () => {
    switch (activeTab) {
      case "active":
        return { pickups: activePickups, deliveries: activeDeliveries };
      case "pickups":
        return { pickups: allPickups, deliveries: [] };
      case "deliveries":
        return { pickups: [], deliveries: allDeliveries };
      case "completed":
        return { pickups: completedPickups, deliveries: completedDeliveries };
      default:
        return { pickups: allPickups, deliveries: allDeliveries };
    }
  };

  const { pickups: displayPickups, deliveries: displayDeliveries } = getVisible();
  const isEmpty = displayPickups.length === 0 && displayDeliveries.length === 0;

  const tabCount = (key) => {
    if (key === "all")        return allPickups.length + allDeliveries.length;
    if (key === "active")     return activePickups.length + activeDeliveries.length;
    if (key === "pickups")    return allPickups.length;
    if (key === "deliveries") return allDeliveries.length;
    if (key === "completed")  return completedPickups.length + completedDeliveries.length;
    return 0;
  };

  return (
    <div className="driver-dashboard">
      {/* ── Sidebar ── */}
      <aside className="driver-sidebar">
        <div className="sidebar-logo">
          <span className="sidebar-logo-icon">🚗</span>
          <div>
            <div className="sidebar-logo-text">CarYugo</div>
            <div className="sidebar-logo-badge">Driver Portal</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {TABS.map(tab => {
            const count = tabCount(tab.key);
            return (
              <button
                key={tab.key}
                className={`sidebar-nav-item ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <span>{tab.icon}</span> {tab.label}
                {count > 0 && <span className="nav-count">{count}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-spacer" />

        <div className="sidebar-driver-info">
          <div className="sidebar-avatar">
            {driver?.name?.[0]?.toUpperCase() || "D"}
          </div>
          <div className="sidebar-driver-details">
            <div className="sidebar-driver-name">{driver?.name || "Driver"}</div>
            <div className="sidebar-driver-email">{driver?.email || driver?.phone}</div>
          </div>
        </div>

        <button className="sidebar-logout" onClick={handleLogout}>
          🚪 Sign Out
        </button>
      </aside>

      {/* ── Main ── */}
      <main className="driver-main">
        <header className="driver-topbar">
          <div>
            <h1 className="topbar-greeting">
              Good day, {driver?.name?.split(" ")[0] || "Driver"} 👋
            </h1>
            <p className="topbar-date">{today}</p>
          </div>
          <button className="refresh-btn" onClick={fetchTasks} disabled={loading}>
            <span className={loading ? "spin" : ""}>🔄</span> Refresh
          </button>
        </header>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat-card stat-card--blue">
            <div className="stat-icon">🔼</div>
            <div className="stat-val">{activePickups.length}</div>
            <div className="stat-label">Active Pickups</div>
          </div>
          <div className="stat-card stat-card--teal">
            <div className="stat-icon">🚚</div>
            <div className="stat-val">{activeDeliveries.length}</div>
            <div className="stat-label">Active Deliveries</div>
          </div>
          <div className="stat-card stat-card--green">
            <div className="stat-icon">✅</div>
            <div className="stat-val">{completedPickups.length + completedDeliveries.length}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card stat-card--total">
            <div className="stat-icon">📋</div>
            <div className="stat-val">{allPickups.length + allDeliveries.length}</div>
            <div className="stat-label">Total Assigned</div>
          </div>
        </div>

        {error && (
          <div className="dashboard-error">
            ⚠️ {error}
            <button onClick={fetchTasks} className="retry-btn">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading your tasks…</p>
          </div>
        ) : (
          <div className="tasks-container">

            {/* ── Completed tab: single combined table ── */}
            {activeTab === "completed" ? (
              <>
                <div className="section-header">
                  <h2 className="section-title">
                    <span>✅</span> Completed Tasks
                    <span className="section-count">
                      {completedPickups.length + completedDeliveries.length}
                    </span>
                  </h2>
                  <p className="section-sub">All pickup & delivery tasks you have finished</p>
                </div>
                {isEmpty ? (
                  <EmptyState tab={activeTab} />
                ) : (
                  <CompletedTable
                    pickups={completedPickups}
                    deliveries={completedDeliveries}
                  />
                )}
              </>
            ) : (
              <>
                {/* Pickup table */}
                {displayPickups.length > 0 && (
                  <section className="tasks-section">
                    <div className="section-header">
                      <h2 className="section-title">
                        <span>🔼</span> Pickup Tasks
                        <span className="section-count">{displayPickups.length}</span>
                      </h2>
                      <p className="section-sub">
                        {activePickups.length} active · {completedPickups.length} completed
                      </p>
                    </div>
                    <PickupTable
                      pickups={displayPickups}
                      onUpdateStatus={handleUpdate}
                      updating={updating}
                    />
                  </section>
                )}

                {/* Delivery table */}
                {displayDeliveries.length > 0 && (
                  <section className="tasks-section">
                    <div className="section-header">
                      <h2 className="section-title">
                        <span>🔽</span> Delivery Tasks
                        <span className="section-count">{displayDeliveries.length}</span>
                      </h2>
                      <p className="section-sub">
                        {activeDeliveries.length} active · {completedDeliveries.length} delivered
                      </p>
                    </div>
                    <DeliveryTable
                      deliveries={displayDeliveries}
                      onUpdateStatus={handleUpdate}
                      updating={updating}
                    />
                  </section>
                )}

                {isEmpty && <EmptyState tab={activeTab} />}
              </>
            )}
          </div>
        )}
      </main>

      {toast && (
        <div className={`toast ${toast.ok ? "toast--ok" : "toast--err"}`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function EmptyState({ tab }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{tab === "completed" ? "🏁" : "🎉"}</div>
      <h3>{tab === "completed" ? "Nothing completed yet" : "No tasks here"}</h3>
      <p>
        {tab === "completed"
          ? "Complete your active tasks and they'll appear here."
          : "No tasks assigned in this view. Check back soon!"}
      </p>
    </div>
  );
}